import { useGetUploadSignedUrlForAlternativeRegistrationLazyQuery } from '@exam-center/graphql';
import { useState } from 'react';

interface UploadAlternativeDocumentImageVariables {
  examEventExamineeId: string;
  /** The image binary to upload. Must be the actual Blob, not an object URL string. */
  imageBlob: Blob;
  contentType?: 'image/png' | 'image/jpeg';
}

export function useUploadAlternativeDocumentImage() {
  const [isLoading, setIsLoading] = useState(false);

  const [uploadSignedUrlForAltReg] = useGetUploadSignedUrlForAlternativeRegistrationLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const uploadAlternativeDocumentImage = async (
    variables: UploadAlternativeDocumentImageVariables,
  ) => {
    setIsLoading(true);

    try {
      const { examEventExamineeId, imageBlob } = variables;
      // Derive the content type from the actual blob (falling back to jpeg) so the
      // signed-url request and the PUT header always match the bytes being sent.
      const contentType =
        variables.contentType ?? (imageBlob.type === 'image/png' ? 'image/png' : 'image/jpeg');

      const { data: signedUrlData } = await uploadSignedUrlForAltReg({
        variables: {
          getUploadSignedUrlForAlternativeRegistrationInput: {
            examEventExamineeId: examEventExamineeId,
            contentType,
          },
        },
      });

      if (!signedUrlData?.ecGetUploadSignedUrlForAlternativeRegistration) {
        throw new Error('Failed to get signed url');
      }

      const {
        signedUrl,
        storageObject: { fileName },
      } = signedUrlData.ecGetUploadSignedUrlForAlternativeRegistration;

      if (!fileName) {
        throw new Error('Invalid file name');
      }

      const uploadFileResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: imageBlob,
        headers: {
          'Content-Type': contentType,
        },
      });

      if (!uploadFileResponse.ok) {
        throw new Error('Failed to upload file');
      }

      return { signedUrl, fileName };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    loading: isLoading,
    uploadAlternativeDocumentImage,
  };
}
